'use client'

import { useRef } from 'react'

import { AnalyticsContext } from '@features/analytics.hooks/useAnalytics'

import { InternalAnalyticsProvider } from './InternalAnalyticsProvider/InternalAnalyticsProvider'

import type { ModalState } from '@features/analytics.hooks/useAnalytics'
import type { ClientConfig } from '@features/common.graphql'

/**
 * Props for the AnalyticsProvider component.
 */
export interface AnalyticsProviderProps {
  /**
   * Children of the provider.
   */
  children: React.ReactNode
  /**
   * Flag that indicates if analytics is enabled or not.
   */
  enabled: boolean
  /**
   * Client configuration.
   */
  clientConfig: ClientConfig
}

/**
 * AnalyticsProvider component that sets up analytics context and third-party integrations.
 * GTM is loaded separately via GoogleTagManagerClient in the app layout.
 * @param props - The props for configuring the AnalyticsProvider.
 * @returns The AnalyticsProvider component.
 */
export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  enabled,
  clientConfig,
}) => {
  const value = useRef<ModalState | null>(null)

  const isCodegenSubdomain =
    typeof globalThis !== 'undefined' &&
    'location' in globalThis &&
    globalThis.location.hostname.startsWith('codegen')

  const isFaucetV2Subdomain =
    typeof globalThis !== 'undefined' &&
    'location' in globalThis &&
    globalThis.location.hostname.startsWith('faucet-v2')

  const config = {
    datadog: {
      ...clientConfig.datadog,
      env: clientConfig.env,
      service: clientConfig.service,
      version: clientConfig.version,
    },
    hubspot: clientConfig.hubspot,
  }

  return (
    <AnalyticsContext.Provider value={value}>
      <InternalAnalyticsProvider
        config={config}
        enabled={enabled && !isCodegenSubdomain && !isFaucetV2Subdomain}
      />
      {children}
    </AnalyticsContext.Provider>
  )
}
